import { cloneElement, useState } from 'react'
import { toast } from 'sonner'
import { ModalShell, ModalHead, ModalSection, ModalLabel, ModalFoot } from '@/ui/Modal'
import { Button, Textarea } from '@/ui'
import { Ico } from '@/ui/Ico'
import { useMarkDoNotContact, useRemoveDoNotContact } from '@/shared/api/leads'
import { useColleagues, useMe } from '@/shared/api/session'
import { useCanEdit } from '@/shared/write-access'
import { useBrand } from '@/shared/white-label/useBrand'
import type { DoNotContact, Lead } from '@/shared/types'
import {
  DO_NOT_CONTACT_REASON_MAX, canMarkDoNotContact, canRemoveDoNotContact, markedByLine, reasonOrUndefined,
} from './doNotContactRules'

/** Whether to offer "Markera Kontakta ej": the server's permission, and not in read-only mode. */
export function useCanMarkDoNotContact(): boolean {
  const { data: me } = useMe()
  return useCanEdit() && canMarkDoNotContact(me)
}

/** A colleague's display name for an email: their roster name, else the local part. */
export function usePersonName(): (email: string | null | undefined) => string {
  const { data: colleagues } = useColleagues()
  const { person, anonymised } = useBrand()
  return email => {
    if (!email) return '—'
    if (anonymised) return person(email)
    return colleagues?.users.find(u => u.email.toLowerCase() === email.toLowerCase())?.name
      || email.split('@')[0]
  }
}

interface SheetProps {
  lead: Lead
  /** `mark` sets Kontakta ej; `remove` lifts it, which only an admin may do. */
  mode: 'mark' | 'remove'
  onCancel: () => void
  /** Called once the server has confirmed the write. */
  onDone: () => void
}

/**
 * Sets or lifts the Kontakta ej mark, with an optional reason that lands on
 * the timeline entry. Waits for the server rather than writing optimistically:
 * a seller who marks a lead has to know the mark held before moving on.
 */
export function DoNotContactSheet({ lead, mode, onCancel, onDone }: SheetProps) {
  const canEdit = useCanEdit()
  const mark = useMarkDoNotContact()
  const remove = useRemoveDoNotContact()
  const mutation = mode === 'mark' ? mark : remove
  const [reason, setReason] = useState('')
  const [failed, setFailed] = useState('')

  const save = async () => {
    if (!canEdit || mutation.isPending) return
    setFailed('')
    try {
      await mutation.mutateAsync({ id: lead.id, reason: reasonOrUndefined(reason) })
    } catch (err) {
      const status = (err as { status?: number }).status
      setFailed(
        mode === 'remove' && status === 403
          ? 'Bara en admin kan ta bort markeringen.'
          : err instanceof Error ? err.message : 'Kunde inte spara. Försök igen.',
      )
      return
    }
    toast.success(mode === 'mark'
      ? `Markerade ${lead.company} som Kontakta ej.`
      : `Tog bort Kontakta ej för ${lead.company}.`)
    onDone()
  }

  return (
    <ModalShell onClose={onCancel} narrow>
      <ModalHead
        eyebrow={mode === 'mark' ? 'Markera lead' : 'Ta bort markering'}
        title={<>Kontakta ej · {lead.company}</>}
        onClose={onCancel}
      />
      <ModalSection>
        <p className="text-xs text-ink-700 mt-0 mb-3">
          {mode === 'mark'
            ? 'Leaden tas bort ur alla köer och går inte att ringa eller logga utfall på. Bara en admin kan ta bort markeringen.'
            : 'Leaden kan ringas och loggas igen och kan komma tillbaka i ägarens kö.'}
        </p>
        <ModalLabel>Orsak (valfritt)</ModalLabel>
        <Textarea
          rows={3}
          maxLength={DO_NOT_CONTACT_REASON_MAX}
          placeholder={mode === 'mark'
            ? 'Till exempel: bad att inte bli kontaktad igen'
            : 'Till exempel: markerad av misstag'}
          value={reason}
          onChange={e => setReason(e.target.value)}
          autoFocus
        />
        {failed && <p role="alert" className="text-[11px] text-terracotta mt-2 mb-0">{failed}</p>}
      </ModalSection>
      <ModalFoot
        onCancel={onCancel}
        onSave={() => { void save() }}
        saveDisabled={!canEdit || mutation.isPending}
        saveLabel={mutation.isPending ? 'Sparar…' : mode === 'mark' ? 'Markera Kontakta ej' : 'Ta bort markering'}
      />
    </ModalShell>
  )
}

/**
 * Stands where the outcome buttons would be on a marked lead, in Focus and in
 * the overlay. Admins get the way to lift the mark here.
 */
export function DoNotContactNotice({ lead, mark: dnc }: { lead: Lead; mark: DoNotContact }) {
  const { data: me } = useMe()
  const canEdit = useCanEdit()
  const nameFor = usePersonName()
  const [removing, setRemoving] = useState(false)
  const banIcon = cloneElement(Ico.ban, { width: 14, height: 14 })

  return (
    <>
      <div
        role="status"
        className="flex flex-wrap items-start gap-3 border border-ink-200 border-l-2 border-l-[var(--color-danger)] bg-white px-3 py-2.5"
      >
        <span className="mt-0.5 shrink-0 text-[var(--color-danger)]">{banIcon}</span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-ink-1000">Kontakta ej</div>
          <p className="text-xs text-ink-700 m-0 mt-0.5">
            Kontakten har bett att inte bli kontaktad. Leaden visas inte i någon kö och kan inte loggas.
          </p>
          <p className="text-[11px] text-ink-500 m-0 mt-1">{markedByLine(dnc, nameFor)}</p>
          {dnc.reason && <p className="text-[11px] text-ink-700 m-0 mt-0.5">Orsak: {dnc.reason}</p>}
        </div>
        {canEdit && canRemoveDoNotContact(me) && (
          <Button size="sm" className="shrink-0" onClick={() => setRemoving(true)}>
            Ta bort markering
          </Button>
        )}
      </div>
      {removing && (
        <DoNotContactSheet
          lead={lead}
          mode="remove"
          onCancel={() => setRemoving(false)}
          onDone={() => setRemoving(false)}
        />
      )}
    </>
  )
}

/** Tabell's row badge, beside the source badge. */
export function DoNotContactBadge({ dnc, name }: { dnc: DoNotContact; name: (email: string) => string }) {
  return (
    <span
      className="inline-flex items-center h-[15px] px-1 rounded border text-[9px] font-bold tracking-wide align-middle shrink-0 whitespace-nowrap"
      style={{
        borderColor: 'color-mix(in srgb, var(--color-danger) 40%, transparent)',
        backgroundColor: 'color-mix(in srgb, var(--color-danger) 10%, transparent)',
        color: 'color-mix(in srgb, var(--color-danger) 85%, var(--color-ink-1000))',
      }}
      title={`Kontakta ej · ${markedByLine(dnc, name)}${dnc.reason ? `\nOrsak: ${dnc.reason}` : ''}`}
    >
      Kontakta ej
    </span>
  )
}
